import { useEffect, useRef } from 'react'
import { v4 as uuid } from 'uuid'

import type { AgentChatMessage, AgentChatSession } from '~/api/agentChat'
import { broadcastChannel } from '~/utils/broadcastChannel'

export interface UseAgentChatSyncOptions {
  isResponding: boolean
  messages: AgentChatMessage[]
  onIsRespondingUpdate?: (isResponding: boolean, sessionId: string) => void
  onSessionUpdate?: (sessionId: string) => void
  session: AgentChatSession | null
}

/**
 * Hook to sync agent chat state across browser tabs using BroadcastChannel.
 * Syncs completed messages and isResponding state to prevent duplicate messages
 * and provide awareness when the agent is responding in another tab.
 */
export const useAgentChatSync = ({
  session,
  messages,
  isResponding,
  onSessionUpdate,
  onIsRespondingUpdate,
}: UseAgentChatSyncOptions) => {
  const tabIdRef = useRef<string>(uuid())
  const lastMessageCountRef = useRef<number>(0)
  const lastIsRespondingRef = useRef<boolean>(false)
  const lastCompletedMessageIdRef = useRef<string | null>(null)

  useEffect(() => {
    lastMessageCountRef.current = 0
    lastIsRespondingRef.current = false
    lastCompletedMessageIdRef.current = null
  }, [session?.id])

  // Broadcast message updates to other tabs (both user messages and completions)
  useEffect(() => {
    if (!session?.id) return

    const currentMessageCount = messages.filter((msg) => !msg.isLoading).length

    // Check if the last message is from the user (newly sent) or agent (completed response)
    const lastNonLoadingMessage = [...messages]
      .reverse()
      .find((msg) => !msg.isLoading)

    // Detect message count increase (new user message or first agent chunk)
    if (currentMessageCount > lastMessageCountRef.current) {
      lastMessageCountRef.current = currentMessageCount

      // Only broadcast for user messages, not partial agent messages
      if (lastNonLoadingMessage?.sender === 'user') {
        broadcastChannel.postMessage({
          action: 'agent-chat-message-sent',
          data: {
            sessionId: session.id,
            messageCount: currentMessageCount,
            tabId: tabIdRef.current,
            timestamp: Date.now(),
          },
        })
      }
    }

    // Detect when an agent message becomes complete (for streaming)
    if (
      lastNonLoadingMessage?.sender === 'agent' &&
      lastNonLoadingMessage?.isComplete === true &&
      lastNonLoadingMessage.id !== lastCompletedMessageIdRef.current
    ) {
      lastCompletedMessageIdRef.current = lastNonLoadingMessage.id

      broadcastChannel.postMessage({
        action: 'agent-chat-message-complete',
        data: {
          sessionId: session.id,
          messageCount: currentMessageCount,
          tabId: tabIdRef.current,
          timestamp: Date.now(),
        },
      })
    }
  }, [session?.id, messages])

  // Broadcast isResponding state changes to other tabs
  useEffect(() => {
    if (!session?.id) return

    if (isResponding !== lastIsRespondingRef.current) {
      broadcastChannel.postMessage({
        action: 'agent-chat-responding',
        data: {
          sessionId: session.id,
          isResponding,
          tabId: tabIdRef.current,
          timestamp: Date.now(),
        },
      })

      lastIsRespondingRef.current = isResponding
    }
  }, [session?.id, isResponding])

  // Listen for updates from other tabs
  useEffect(() => {
    const handleMessage = (event: {
      action: string
      data?: {
        isResponding?: boolean
        messageCount?: number
        sessionId?: string
        tabId?: string
        timestamp?: number
      }
    }) => {
      const { action, data } = event

      if (data?.tabId === tabIdRef.current) {
        return
      }

      if (
        (action === 'agent-chat-message-sent' ||
          action === 'agent-chat-message-complete') &&
        data?.sessionId &&
        data?.sessionId === session?.id
      ) {
        onSessionUpdate?.(data.sessionId)
      }

      if (
        action === 'agent-chat-responding' &&
        data?.sessionId &&
        data?.sessionId === session?.id &&
        data?.isResponding !== undefined &&
        data?.tabId
      ) {
        onIsRespondingUpdate?.(data.isResponding, data.sessionId)
      }
    }

    broadcastChannel.addEventListener('message', handleMessage)

    return () => {
      broadcastChannel.removeEventListener('message', handleMessage)
    }
  }, [session?.id, onSessionUpdate, onIsRespondingUpdate])

  return {
    tabId: tabIdRef.current,
  }
}
